import React, { useState, useEffect } from 'react';
import config from '../config';
import { CPC_SECTIONS } from '../constants';

function ComparePanel({ showComparePanel, setShowComparePanel, compareList, savedAiAnalysis, setSavedAiAnalysis }) {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Load saved AI analysis when panel opens
  useEffect(() => {
    console.log('ComparePanel useEffect triggered:', {
      showComparePanel,
      savedAiAnalysis: savedAiAnalysis ? 'EXISTS' : 'NULL',
      savedAiAnalysisLength: savedAiAnalysis ? savedAiAnalysis.length : 0
    });
    
    if (showComparePanel && savedAiAnalysis) {
      console.log('Setting AI analysis from saved data');
      setAiAnalysis(savedAiAnalysis);
    } else if (!showComparePanel) {
      // Clear analysis when panel closes
      setAiAnalysis(null);
      if (setSavedAiAnalysis) setSavedAiAnalysis(null);
    }
  }, [showComparePanel, savedAiAnalysis, setSavedAiAnalysis]);
  
  const handleGenerateAIAnalysis = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to use AI Comparison Analysis');
      return;
    }

    setAnalysisLoading(true);
    
    try {
      const response = await fetch(`${config.API_URL}/api/compare-patents-ai?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patent_numbers: compareList.map(p => p.patent_number || p.id),
          titles: compareList.map(p => p.title),
          abstracts: compareList.map(p => p.abstract || p.summary || ''),
          assignees: compareList.map(p => p.assignee || 'Unknown'),
          cpc_codes: compareList.map(p => p.cpc_codes || p.ipc_codes || [])
        })
      });

      const data = await response.json();
      
      if (response.status === 403) {
        // Not Pro tier - show upgrade prompt
        setShowUpgradePrompt(true);
        setAnalysisLoading(false);
        return;
      }
      
      if (response.status === 429) {
        alert(data.detail || 'Daily limit reached');
        setAnalysisLoading(false);
        return;
      }
      
      if (response.ok && data.success) {
        setAiAnalysis(data.analysis);
      } else {
        alert('Failed to generate analysis: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      alert('Error generating analysis: ' + error.message);
    }
    
    setAnalysisLoading(false);
  };
  
  const handleSaveComparison = async () => {
    const name = prompt('Enter a name for this comparison:', 'Comparison ' + new Date().toLocaleDateString());
    if (!name) return;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save comparisons');
      return;
    }
    try {
      const response = await fetch(config.API_URL + '/api/save-comparison?token=' + token, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: name,
    patent_numbers: compareList.map(p => p.patent_number || p.id),
    your_patent_number: null, // We don't have "your patent" context in ComparePanel
    ai_analysis: aiAnalysis
  })
});
      const data = await response.json();
      if (response.ok) {
        alert('Comparison saved!');
      } else {
        alert('Failed to save: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Error saving comparison: ' + error.message);
    }
  };

  if (!showComparePanel || compareList.length < 2) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'white',
      zIndex: 950,
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1e293b',
        color: 'white',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>Patent Comparison</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleGenerateAIAnalysis}
            disabled={analysisLoading}
            style={{
              backgroundColor: analysisLoading ? '#94a3b8' : '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: analysisLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {analysisLoading ? 'Analyzing...' : '✨ AI Analysis'}
          </button>
          <button
            onClick={handleSaveComparison}
            style={{
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Save Comparison
          </button>
          <button
            onClick={() => setShowComparePanel(false)}
            style={{
              backgroundColor: '#475569',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* AI Analysis Section */}
      {aiAnalysis && (
        <div style={{
          margin: '24px',
          padding: '24px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px solid #8b5cf6'
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '18px',
            fontWeight: '700',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ✨ AI Comparative Analysis
          </h3>
          <div style={{
            fontSize: '14px',
            lineHeight: '1.7',
            color: '#334155',
            whiteSpace: 'pre-wrap'
          }}>
            {aiAnalysis}
          </div>
        </div>
      )}

      {/* Comparison Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: compareList.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr',
        gap: '1px',
        backgroundColor: '#e2e8f0'
      }}>
        {compareList.map((patent, index) => (
          <div key={patent.id} style={{ backgroundColor: 'white' }}>
            {/* Patent Header */}
            <div style={{
              backgroundColor: index === 0 ? '#0369a1' : index === 1 ? '#7c3aed' : '#059669',
              color: 'white',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>{patent.id}</div>
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
                {patent.status || 'Unknown Status'}
              </div>
            </div>

            {/* Title */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>TITLE</div>
              <div style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.4' }}>{patent.title}</div>
            </div>

            {/* Assignee */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>ASSIGNEE</div>
              <div style={{ fontSize: '14px', color: '#1e293b' }}>{patent.assignee || 'Not listed'}</div>
            </div>

            {/* Dates */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>DATES</div>
              <div style={{ fontSize: '13px', color: '#1e293b' }}>
                <div>Filed: {patent.filing_date || 'Unknown'}</div>
                <div>Granted: {patent.grant_date || 'Unknown'}</div>
              </div>
            </div>

            {/* CPC Codes */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', fontWeight: '600' }}>CPC CODES</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {(patent.cpc_codes || patent.ipc_codes || []).slice(0, 8).map((code, i) => {
                  const isShared = compareList.filter(p => p.id !== patent.id)
                    .some(p => (p.cpc_codes || p.ipc_codes || []).includes(code));
                  return (
                    <span key={i} title={code + ': ' + (CPC_SECTIONS[code.charAt(0)] || 'Unknown Section')} style={{
                      backgroundColor: isShared ? '#fef3c7' : '#f1f5f9',
                      border: isShared ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      color: isShared ? '#92400e' : '#475569'
                    }}>
                      {code}
                    </span>
                  );
                })}
              </div>
              {(patent.ipc_codes || patent.cpc_codes || []).length > 8 && (
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  +{(patent.ipc_codes || patent.cpc_codes || []).length - 8} more
                </div>
              )}
            </div>

            {/* Abstract */}
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>ABSTRACT</div>
              <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', maxHeight: '200px', overflow: 'auto' }}>
                {patent.summary || patent.abstract || 'No abstract available'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        fontSize: '13px'
      }}>
        <span style={{ fontWeight: '600', color: '#475569' }}>Legend:</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>CPC</span>
          <span style={{ color: '#64748b' }}>= Shared between patents</span>
        </span>
      </div>

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
              Pro Feature Required
            </h3>
            <p style={{ fontSize: '16px', color: '#475569', marginBottom: '24px', lineHeight: '1.6' }}>
              AI Comparison Analysis requires Pro. Upgrade to unlock AI-powered comparative insights that reveal overlaps, differences, and commercial implications.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => window.location.href = '/pricing'}
                style={{
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Upgrade to Pro
              </button>
              <button
                onClick={() => setShowUpgradePrompt(false)}
                style={{
                  backgroundColor: 'white',
                  color: '#475569',
                  border: '2px solid #e2e8f0',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComparePanel;